import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { TitleSidebar } from "./TitleDashboardPage";
import {
  ArrowLeft,
  Building2,
  User,
  DollarSign,
  Calendar,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  Edit2,
  Save,
  X,
  Plus,
  Trash2,
  ChevronRight,
  FileSearch,
  Shield,
  Briefcase
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Stage definitions
const STAGES = [
  { key: "order_received", label: "Order Received", icon: FileText },
  { key: "title_search", label: "Title Search", icon: FileSearch },
  { key: "search_review", label: "Search Review", icon: Shield },
  { key: "commitment_issued", label: "Commitment Issued", icon: FileText },
  { key: "requirements_pending", label: "Requirements Pending", icon: AlertTriangle },
  { key: "clear_to_close", label: "Clear to Close", icon: CheckCircle },
  { key: "closing_scheduled", label: "Closing Scheduled", icon: Calendar },
  { key: "closing_complete", label: "Closing Complete", icon: Briefcase },
  { key: "policy_issued", label: "Policy Issued", icon: Shield }
];

const STAGE_COLORS = {
  order_received: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500",
  title_search: "bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border-purple-500",
  search_review: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500",
  commitment_issued: "bg-cyan-500/20 text-cyan-400 border-cyan-500",
  requirements_pending: "bg-orange-500/20 text-orange-400 border-orange-500",
  clear_to_close: "bg-green-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-green-500",
  closing_scheduled: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500",
  closing_complete: "bg-teal-500/20 text-teal-400 border-teal-500",
  policy_issued: "bg-white/20/20 text-white/50 border-white/[0.15]"
};

// Stage Progress Bar
function StageProgressBar({ currentStage }) {
  const currentIndex = STAGES.findIndex(s => s.key === currentStage);

  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6">
      <div className="flex items-center justify-between overflow-x-auto pb-2">
        {STAGES.map((stage, index) => {
          const Icon = stage.icon;
          const isComplete = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isPending = index > currentIndex;

          return (
            <div key={stage.key} className="flex items-center">
              <div className="flex flex-col items-center min-w-[80px]">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                  isComplete ? "bg-emerald-500 text-white" :
                  isCurrent ? "bg-emerald-500/20 border-2 border-emerald-500 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
                  "bg-white/[0.08] text-white/40"
                }`}>
                  {isComplete ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className={`text-[10px] text-center whitespace-nowrap ${
                  isCurrent ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-medium" :
                  isComplete ? "text-white/70" : "text-white/40"
                }`}>
                  {stage.label}
                </span>
              </div>
              {index < STAGES.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 ${
                  isComplete ? "bg-emerald-500" : "bg-white/[0.08]"
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Info Card Component
function InfoCard({ title, icon: Icon, iconColor, children }) {
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
      <h3 className={`font-medium mb-3 flex items-center gap-2 ${iconColor}`}>
        <Icon className="w-4 h-4" /> {title}
      </h3>
      {children}
    </div>
  );
}

// Stage Update Modal
function StageUpdateModal({ isOpen, onClose, currentStage, onUpdate }) {
  const [selectedStage, setSelectedStage] = useState(currentStage);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    await onUpdate(selectedStage, notes);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-md">
        <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">Update Stage</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <Label className="text-white/70">New Stage</Label>
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              className="w-full bg-black border border-white/[0.08] text-white px-3 py-2 mt-1"
              data-testid="stage-update-select"
            >
              {STAGES.map((stage) => (
                <option key={stage.key} value={stage.key}>
                  {stage.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-white/70">Notes</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-black border border-white/[0.08] text-white px-3 py-2 mt-1 min-h-[80px]"
              placeholder="Add notes about this stage update..."
              data-testid="stage-notes-input"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="border-white/[0.1] rounded-none">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-none"
              data-testid="update-stage-btn"
            >
              {loading ? "Updating..." : "Update Stage"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Add Lien Modal
function AddLienModal({ isOpen, onClose, onAdd }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    lien_type: "mortgage",
    lienholder: "",
    amount: "",
    recording_date: "",
    document_number: "",
    notes: ""
  });

  const handleSubmit = async () => {
    setLoading(true);
    await onAdd({
      ...formData,
      amount: parseFloat(formData.amount) || 0
    });
    setLoading(false);
    onClose();
    setFormData({
      lien_type: "mortgage",
      lienholder: "",
      amount: "",
      recording_date: "",
      document_number: "",
      notes: ""
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-md">
        <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">Add Lien</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70">Lien Type</Label>
              <select
                value={formData.lien_type}
                onChange={(e) => setFormData({ ...formData, lien_type: e.target.value })}
                className="w-full bg-black border border-white/[0.08] text-white px-3 py-2 mt-1"
              >
                <option value="mortgage">Mortgage</option>
                <option value="tax">Tax Lien</option>
                <option value="mechanic">Mechanic's Lien</option>
                <option value="judgment">Judgment</option>
                <option value="hoa">HOA Lien</option>
              </select>
            </div>
            <div>
              <Label className="text-white/70">Amount</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="bg-black border-white/[0.08] text-white mt-1"
                placeholder="150000"
              />
            </div>
          </div>
          <div>
            <Label className="text-white/70">Lienholder</Label>
            <Input
              value={formData.lienholder}
              onChange={(e) => setFormData({ ...formData, lienholder: e.target.value })}
              className="bg-black border-white/[0.08] text-white mt-1"
              placeholder="ABC Bank"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70">Recording Date</Label>
              <Input
                type="date"
                value={formData.recording_date}
                onChange={(e) => setFormData({ ...formData, recording_date: e.target.value })}
                className="bg-black border-white/[0.08] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/70">Document #</Label>
              <Input
                value={formData.document_number}
                onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                className="bg-black border-white/[0.08] text-white mt-1"
                placeholder="2024-12345"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="border-white/[0.1] rounded-none">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-none"
            >
              {loading ? "Adding..." : "Add Lien"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TitleOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showLienModal, setShowLienModal] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, [orderId]);

  const fetchData = async () => {
    try {
      const [profileRes, orderRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/title/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/title/orders/${orderId}`, { withCredentials: true })
      ]);

      setCompany(profileRes.data);
      setOrder(orderRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/title");
      } else if (error.response?.status === 404) {
        toast.error("Order not found");
        navigate("/title/orders");
      } else {
        toast.error("Failed to load order");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStageUpdate = async (newStage, notes) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/title/orders/${orderId}/stage`,
        { stage: newStage, notes },
        { withCredentials: true }
      );
      toast.success("Stage updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update stage");
    }
  };

  const handleAddLien = async (lienData) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/title/orders/${orderId}/liens`,
        lienData,
        { withCredentials: true }
      );
      toast.success("Lien added");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add lien");
    }
  };

  const handleUpdateFees = async (fees) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/title/orders/${orderId}/fees`,
        fees,
        { withCredentials: true }
      );
      toast.success("Fees updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update fees");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/title/auth/logout`, {}, { withCredentials: true });
      navigate("/title");
    } catch (error) {
      navigate("/title");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "search", label: "Title Search" },
    { key: "documents", label: "Documents" },
    { key: "fees", label: "Fees" },
    { key: "history", label: "History" }
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="title-order-detail">
      <TitleSidebar company={company} onLogout={handleLogout} />

      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/title/orders" className="text-white/50 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-white">{order.property_address}</h1>
            <p className="text-white/50 mt-1">
              {order.order_type} • Created {new Date(order.created_at).toLocaleDateString()}
            </p>
          </div>
          <Button
            onClick={() => setShowStageModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-none"
            data-testid="update-stage-header-btn"
          >
            Update Stage
          </Button>
        </div>

        {/* Stage Progress */}
        <StageProgressBar currentStage={order.stage} />

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/[0.08]">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium transition-all duration-300 ${
                activeTab === tab.key
                  ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-b-2 border-emerald-500"
                  : "text-white/50 hover:text-white"
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Property Info */}
            <InfoCard title="Property Information" icon={Building2} iconColor="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Address</span>
                  <span className="text-white">{order.property_address}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Sale Price</span>
                  <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-mono">{formatCurrency(order.sale_price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Order Type</span>
                  <span className="text-white capitalize">{order.order_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Expected Close</span>
                  <span className="text-white">{order.expected_close_date || "Not set"}</span>
                </div>
              </div>
            </InfoCard>

            {/* Buyer Info */}
            <InfoCard title="Buyer" icon={User} iconColor="text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Name</span>
                  <span className="text-white">{order.buyer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Email</span>
                  <span className="text-white">{order.buyer_email || "Not provided"}</span>
                </div>
              </div>
            </InfoCard>

            {/* Seller Info */}
            <InfoCard title="Seller" icon={User} iconColor="text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Name</span>
                  <span className="text-white">{order.seller_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Email</span>
                  <span className="text-white">{order.seller_email || "Not provided"}</span>
                </div>
              </div>
            </InfoCard>

            {/* Financing Info */}
            <InfoCard title="Financing" icon={DollarSign} iconColor="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Loan Amount</span>
                  <span className="text-white font-mono">{order.loan_amount ? formatCurrency(order.loan_amount) : "Cash"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Lender</span>
                  <span className="text-white">{order.lender_name || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Realtor</span>
                  <span className="text-white">{order.realtor_name || "N/A"}</span>
                </div>
              </div>
            </InfoCard>
          </div>
        )}

        {activeTab === "search" && (
          <div className="space-y-6">
            {/* Title Search Status */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-white">Title Search Status</h3>
                <span className={`px-3 py-1 text-sm font-medium ${
                  order.title_search?.status === "completed" ? "bg-green-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
                  order.title_search?.status === "in_progress" ? "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" :
                  order.title_search?.status === "issues_found" ? "bg-red-500/20 text-[#FF3B30]" :
                  "bg-white/[0.08] text-white/70"
                }`}>
                  {order.title_search?.status || "Pending"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-white/50">Started</span>
                  <p className="text-white">{order.title_search?.started_at ? new Date(order.title_search.started_at).toLocaleString() : "Not started"}</p>
                </div>
                <div>
                  <span className="text-white/50">Completed</span>
                  <p className="text-white">{order.title_search?.completed_at ? new Date(order.title_search.completed_at).toLocaleString() : "In progress"}</p>
                </div>
              </div>
            </div>

            {/* Liens */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-white">Liens & Encumbrances</h3>
                <Button
                  onClick={() => setShowLienModal(true)}
                  variant="outline"
                  className="border-emerald-500 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] hover:bg-emerald-500/10 rounded-none"
                  data-testid="add-lien-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Lien
                </Button>
              </div>

              {order.title_search?.liens?.length > 0 ? (
                <div className="space-y-3">
                  {order.title_search.liens.map((lien) => (
                    <div key={lien.lien_id} className="bg-black border border-white/[0.08] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium capitalize">{lien.lien_type} Lien</span>
                        <span className={`px-2 py-1 text-xs ${
                          lien.status === "released" ? "bg-green-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
                          lien.status === "payoff_ordered" ? "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" :
                          "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]"
                        }`}>
                          {lien.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-white/50">Lienholder:</span>
                          <span className="text-white ml-2">{lien.lienholder}</span>
                        </div>
                        <div>
                          <span className="text-white/50">Amount:</span>
                          <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] ml-2 font-mono">{formatCurrency(lien.amount)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/50 text-center py-8">No liens recorded</p>
              )}
            </div>
          </div>
        )}

        {activeTab === "documents" && (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-white">Documents</h3>
              <Button variant="outline" className="border-emerald-500 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] hover:bg-emerald-500/10 rounded-none">
                <Plus className="w-4 h-4 mr-2" /> Upload Document
              </Button>
            </div>

            {order.documents?.length > 0 ? (
              <div className="space-y-2">
                {order.documents.map((doc) => (
                  <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-black border border-white/[0.08]">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-white/50" />
                      <div>
                        <p className="text-white">{doc.name}</p>
                        <p className="text-white/40 text-xs capitalize">{doc.type}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs ${
                      doc.status === "approved" ? "bg-green-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
                      doc.status === "rejected" ? "bg-red-500/20 text-[#FF3B30]" :
                      "bg-white/[0.08] text-white/70"
                    }`}>
                      {doc.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/50 text-center py-8">No documents uploaded</p>
            )}
          </div>
        )}

        {activeTab === "fees" && (
          <FeesTab order={order} onUpdate={handleUpdateFees} />
        )}

        {activeTab === "history" && (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <h3 className="font-heading text-lg font-bold text-white mb-4">Stage History</h3>
            <div className="space-y-4">
              {order.stage_history?.map((entry, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    {index < order.stage_history.length - 1 && (
                      <div className="w-0.5 flex-1 bg-white/[0.08] mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <p className="text-white font-medium capitalize">
                      {entry.stage.replace(/_/g, " ")}
                    </p>
                    <p className="text-white/50 text-sm">
                      {new Date(entry.date).toLocaleString()}
                    </p>
                    {entry.notes && (
                      <p className="text-white/40 text-sm mt-1">{entry.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <StageUpdateModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        currentStage={order.stage}
        onUpdate={handleStageUpdate}
      />

      <AddLienModal
        isOpen={showLienModal}
        onClose={() => setShowLienModal(false)}
        onAdd={handleAddLien}
      />
    </div>
  );
}

// Fees Tab Component
function FeesTab({ order, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [fees, setFees] = useState({
    title_search_fee: order.fees?.title_search_fee || 0,
    title_insurance_premium: order.fees?.title_insurance_premium || 0,
    closing_fee: order.fees?.closing_fee || 0,
    recording_fee: order.fees?.recording_fee || 0,
    other_fees: order.fees?.other_fees || 0
  });

  const total = Object.values(fees).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  const handleSave = async () => {
    await onUpdate(fees);
    setEditing(false);
  };

  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-white">Fee Schedule</h3>
        <Button
          onClick={() => editing ? handleSave() : setEditing(true)}
          variant={editing ? "default" : "outline"}
          className={editing ? "bg-emerald-600 hover:bg-emerald-700 rounded-none" : "border-emerald-500 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] rounded-none"}
          data-testid="edit-fees-btn"
        >
          {editing ? <><Save className="w-4 h-4 mr-2" /> Save</> : <><Edit2 className="w-4 h-4 mr-2" /> Edit</>}
        </Button>
      </div>

      <div className="space-y-4">
        {[
          { key: "title_search_fee", label: "Title Search Fee" },
          { key: "title_insurance_premium", label: "Title Insurance Premium" },
          { key: "closing_fee", label: "Closing Fee" },
          { key: "recording_fee", label: "Recording Fee" },
          { key: "other_fees", label: "Other Fees" }
        ].map((item) => (
          <div key={item.key} className="flex items-center justify-between py-2 border-b border-white/[0.08]">
            <span className="text-white/50">{item.label}</span>
            {editing ? (
              <Input
                type="number"
                value={fees[item.key]}
                onChange={(e) => setFees({ ...fees, [item.key]: e.target.value })}
                className="w-32 bg-black border-white/[0.08] text-white text-right"
              />
            ) : (
              <span className="text-white font-mono">{formatCurrency(fees[item.key])}</span>
            )}
          </div>
        ))}

        <div className="flex items-center justify-between pt-4 border-t-2 border-white/[0.1]">
          <span className="text-white font-bold">Total</span>
          <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-mono font-bold text-xl">{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );
}
